import { LicenseCollection } from "@courses-models/license";

import { AuthenticatedEkeyUserModel, AuthenticatedUnicUserModel } from "@courses-utils/gateway-auth/src/ts/models";

/**
 * Helper that'll return a license collection, which contains the list of
 * products that this user has been authorized access to.
 */
export async function getAuthorizedProductIds(
    user: AuthenticatedEkeyUserModel | AuthenticatedUnicUserModel | undefined,
): Promise<string[]> {
    // Bail out if the user wasn't authenticated
    if (!user) {
        return [];
    }

    // ... Otherwise fetch a list of licenses
    let licenses: LicenseCollection | undefined;

    if (user instanceof AuthenticatedEkeyUserModel) {
        licenses = await LicenseCollection.GET_BY_EKEY_USER_ID(user.ekeyId);
    } else if (user instanceof AuthenticatedUnicUserModel) {
        licenses = await LicenseCollection.GET_BY_UNIC_USER_ID(user.id);
    }

    // If no license collection was found, then return an empty list of
    // authorized product ids
    if (!licenses) {
        return [];
    }

    // ... Otherwise loop over the license collection, and generate list of
    // authorized product ids
    const authorizedProductIds: string[] = [];

    for (const license of licenses) {
        if (license.id.length === 10) {
            // If an isbn-10 was given, then push both isbn-13 (by prepending 978 to
            // the isbn-10) and the raw isbn-10 to list of product ids
            authorizedProductIds.push(`978${license.id}`);
            authorizedProductIds.push(license.id);
        } else if (
            license.id.length === 13 &&
            license.id.substr(0, 3) === "978"
        ) {
            // If an isbn-13 was given, then push both the raw isbn-13 and isbn-10
            // (by removing the 978 prefix) to list of product ids
            authorizedProductIds.push(license.id);
            authorizedProductIds.push(license.id.substr(3));
        } else {
            // ... Otherwise if a non-isbn was given, then look up only this id
            authorizedProductIds.push(license.id);
        }
    }

    // Return the list of authorized products
    return authorizedProductIds;
}
